import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Box, Typography } from "@mui/material";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import NumericInputField from "../common/NumericInputField";
import CustomAutoComplete from "../common/CustomAutoComplete";
import IOSSwitch from "../common/IOSSwitch";
import { ActiveIngredient } from "./types";

interface ActiveIngredientsWidgetProps {
  title?: string;
  activeIngredients: ActiveIngredient[];
  onChange: (ingredients: ActiveIngredient[]) => void;
  serviceType?: string;
}

// Constants
const TYPING_DELAY_MS = 1000;

// Default unit options for IV Therapy and Pharmaceuticals IV
const DEFAULT_UNIT_OPTIONS = ["mg", "mcg", "mEq", "%", "g/dL", "mg/mL", "mcg/mL"];

// Service type specific unit options
const getUnitOptions = (serviceType?: string): string[] => {
  switch (serviceType) {
    case "Pharmaceuticals":
      return ["mg", "mcg", "mL", "mg/mL", "mcg/mL", "ug/mL"];
    case "Supplements":
      return ["mg", "mcg", "IU", "mL"];
    case "IV Therapy":
    case "Pharmaceuticals IV":
    default:
      return DEFAULT_UNIT_OPTIONS;
  }
};

const ActiveIngredientsWidget: React.FC<ActiveIngredientsWidgetProps> = ({
  activeIngredients,
  onChange,
  title = "Active Compound(s)",
  serviceType,
}) => {
  const theme: Theme = useTheme();

  // Calculate unit options based on service type
  const unitOptions = useMemo(() => getUnitOptions(serviceType), [serviceType]);

  // Initialize switch states based on initial field values
  const getInitialSwitchState = (ingredient: ActiveIngredient): boolean => {
    // If value is not null, it's single mode (switch off)
    if (ingredient.value !== null && ingredient.value !== 0) {
      return false;
    }
    // If min and max are not null, it's range mode (switch on)
    if (ingredient.min !== null && ingredient.max !== null) {
      return true;
    }
    // Default to single mode (switch off)
    return false;
  };

  // State to track switch mode for each ingredient
  const [switchStates, setSwitchStates] = useState<boolean[]>(() =>
    activeIngredients.map(getInitialSwitchState)
  );

  // State to track if user is currently typing (to delay validation)
  const [isTyping, setIsTyping] = useState<{
    [key: string]: { value: boolean; min: boolean; max: boolean };
  }>({});
  const typingTimeoutRef = useRef<{
    [key: string]: {
      value?: NodeJS.Timeout;
      min?: NodeJS.Timeout;
      max?: NodeJS.Timeout;
    };
  }>({});

  // Update switch states when activeIngredients array changes
  useEffect(() => {
    setSwitchStates(activeIngredients.map(getInitialSwitchState));
  }, [activeIngredients.length]); // Only update when array length changes

  const updateIngredient = (
    index: number,
    updates: Partial<ActiveIngredient>
  ) => {
    const updatedIngredients = [...activeIngredients];
    updatedIngredients[index] = { ...updatedIngredients[index], ...updates };
    onChange(updatedIngredients);
  };

  // Function to handle typing delay
  const handleTypingDelay = useCallback(
    (ingredientIndex: number, field: "value" | "min" | "max") => {
      const key = `${ingredientIndex}`;

      // Initialize typing state for this ingredient if it doesn't exist
      setIsTyping((prev) => ({
        ...prev,
        [key]: {
          value: prev[key]?.value || false,
          min: prev[key]?.min || false,
          max: prev[key]?.max || false,
          [field]: true,
        },
      }));

      // Initialize timeout ref for this ingredient if it doesn't exist
      if (!typingTimeoutRef.current[key]) {
        typingTimeoutRef.current[key] = {};
      }

      // Clear existing timeout
      if (typingTimeoutRef.current[key][field]) {
        clearTimeout(typingTimeoutRef.current[key][field]);
      }

      // Set new timeout to stop "typing" state after delay
      typingTimeoutRef.current[key][field] = setTimeout(() => {
        setIsTyping((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            [field]: false,
          },
        }));
      }, TYPING_DELAY_MS);
    },
    []
  );

  // Validation functions
  const validateValue = useCallback(
    (value: number | null, hasUserInput: boolean): string => {
      if (!hasUserInput && (value === null || value === 0)) return "";
      if (value !== null && value < 0) {
        return "Value must be positive";
      }
      return "";
    },
    []
  );

  const validateMinValue = useCallback(
    (
      minValue: number | null,
      maxValue: number | null,
      hasUserInput: boolean
    ): string => {
      if (!hasUserInput && (minValue === null || minValue === 0)) return "";
      if (minValue !== null && minValue < 0) {
        return "Min value must be positive";
      }
      return "";
    },
    []
  );

  const validateMaxValue = useCallback(
    (
      maxValue: number | null,
      minValue: number | null,
      hasUserInput: boolean
    ): string => {
      if (!hasUserInput && (maxValue === null || maxValue === 0)) return "";
      if (maxValue !== null && maxValue < 0) {
        return "Max value must be positive";
      }
      if (
        maxValue !== null &&
        minValue !== null &&
        minValue > 0 &&
        maxValue <= minValue
      ) {
        return "Max value must be greater than min value";
      }
      return "";
    },
    []
  );

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(typingTimeoutRef.current).forEach((timeouts) => {
        if (timeouts) {
          Object.values(timeouts).forEach((timeout) => {
            if (timeout) clearTimeout(timeout);
          });
        }
      });
    };
  }, []);

  // Helper function to get user input state
  const hasUserInput = useCallback((value: number | null): boolean => {
    return value !== null && value !== 0;
  }, []);

  const handleSwitchChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isRange = event.target.checked;

    // Update switch state
    setSwitchStates((prev) => {
      const newStates = [...prev];
      newStates[index] = isRange;
      return newStates;
    });

    // Reset all fields to 0 when switching modes
    updateIngredient(index, {
      value: 0,
      min: 0,
      max: 0,
    });
  };

  // Enhanced input change handlers with validation
  const handleValueChange = useCallback(
    (
      index: number,
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      handleTypingDelay(index, "value");
      const value = e.target.value;
      updateIngredient(index, {
        value: value === "" ? null : parseFloat(value),
      });
    },
    [handleTypingDelay, updateIngredient]
  );

  const handleMinChange = useCallback(
    (
      index: number,
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      handleTypingDelay(index, "min");
      const value = e.target.value;
      updateIngredient(index, {
        min: value === "" ? null : parseFloat(value),
      });
    },
    [handleTypingDelay, updateIngredient]
  );

  const handleMaxChange = useCallback(
    (
      index: number,
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      handleTypingDelay(index, "max");
      const value = e.target.value;
      updateIngredient(index, {
        max: value === "" ? null : parseFloat(value),
      });
    },
    [handleTypingDelay, updateIngredient]
  );

  // Don't render if no active ingredients
  if (!activeIngredients || activeIngredients.length === 0) {
    return null;
  }

  return (
    <Box>
      <Box sx={styles.sectionHeader}>
        <Typography sx={styles.sectionTitle(theme)}>{title}</Typography>
      </Box>

      {activeIngredients.map((ingredient, index) => {
        const isRange = switchStates[index] || false;
        const typingState = isTyping[`${index}`] || {
          value: false,
          min: false,
          max: false,
        };

        // Calculate validation errors
        const valueError =
          !isRange && !typingState.value
            ? validateValue(ingredient.value, hasUserInput(ingredient.value))
            : "";
        const minError =
          isRange && !typingState.min
            ? validateMinValue(
                ingredient.min,
                ingredient.max,
                hasUserInput(ingredient.min)
              )
            : "";
        const maxError =
          isRange && !typingState.max
            ? validateMaxValue(
                ingredient.max,
                ingredient.min,
                hasUserInput(ingredient.max)
              )
            : "";

        // Combine all errors for display
        const errors = [valueError, minError, maxError].filter(Boolean);
        const hasErrors = errors.length > 0;
        const combinedErrorMessage = errors.join(" and ");

        return (
          <Box key={ingredient.id || index} sx={styles.ingredientCard(theme)}>
            {/* Toggle between Range and Single */}
            <IOSSwitch
              checked={isRange}
              onChange={(e) => handleSwitchChange(index, e)}
              leftLabel="Single"
              rightLabel="Range"
              showLabels={true}
            />
            {/* Input Container */}
            <Box sx={styles.ingredientCardContent}>
              {!isRange ? (
                // Single mode
                <Box sx={styles.inputContainer}>
                  <Box sx={styles.inputWrapper}>
                    <NumericInputField
                      label="Value"
                      value={ingredient.value?.toString() || ""}
                      handleChange={(e) => handleValueChange(index, e)}
                      name="value"
                      error={valueError ? "error" : ""}
                      allowNegative={false}
                    />
                  </Box>
                  <Box sx={styles.inputWrapper}>
                    <CustomAutoComplete
                      label="Unit"
                      options={unitOptions}
                      value={ingredient.unit || null}
                      onChange={(value) =>
                        updateIngredient(index, { unit: value || "" })
                      }
                      placeholder="Select unit"
                    />
                  </Box>
                </Box>
              ) : (
                // Range mode
                <Box sx={styles.inputContainer}>
                  <Box sx={styles.inputWrapper}>
                    <NumericInputField
                      label="Min"
                      value={ingredient.min?.toString() || ""}
                      handleChange={(e) => handleMinChange(index, e)}
                      name="minValue"
                      error={minError ? "error" : ""}
                      allowNegative={false}
                    />
                  </Box>
                  <Box sx={styles.inputWrapper}>
                    <NumericInputField
                      label="Max"
                      value={ingredient.max?.toString() || ""}
                      handleChange={(e) => handleMaxChange(index, e)}
                      name="maxValue"
                      error={maxError ? "error" : ""}
                      allowNegative={false}
                    />
                  </Box>
                  <Box sx={styles.inputWrapper}>
                    <CustomAutoComplete
                      label="Unit"
                      options={unitOptions}
                      value={ingredient.unit || null}
                      onChange={(value) =>
                        updateIngredient(index, { unit: value || "" })
                      }
                      placeholder="Select unit"
                    />
                  </Box>
                </Box>
              )}
              {/* Ingredient Name Display with Info Icon */}
              <Box sx={styles.ingredientLabelContainer(theme)}>
                <Box sx={styles.ingredientLabel(theme)}>{ingredient.name}</Box>
              </Box>
            </Box>
            {/* Error display */}

            <Box sx={styles.errorText(theme)}>
              {hasErrors ? combinedErrorMessage : ""}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const styles = {
  sectionHeader: {
    marginBottom: "16px",
  },
  sectionTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  ingredientCard: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
    width: "100%",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
  }),
  ingredientCardContent: {
    display: "flex",
    flexDirection: "row" as "row",
    gap: "16px",
    alignItems: "stretch",
    [`@media (max-width:600px)`]: {
      flexDirection: "column" as "column",
      gap: "8px",
    },
  },
  inputContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "row" as "row",
    gap: "8px",
  },
  inputWrapper: {
    flex: 1,
    minWidth: 0,
  },
  ingredientLabelContainer: (theme: Theme) => ({
    flex: 0.8,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "8px 12px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    backgroundColor: theme.colorTokens.N[30],
  }),
  ingredientLabel: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
  }),
  errorText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.DANGER_GRAPH_DEFICIT,
    marginTop: "-6px",
    height: "20px",
  }),
};

export default ActiveIngredientsWidget;
